import { CustomFieldResourceType } from "@repo/app-types/entities/customField";
import {
  CustomerInteractionSplitting,
  type CustomerInteractionSplitting as CustomerInteractionSplittingType,
} from "@repo/app-types/entities/playbook";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { RadioButtonsGroup } from "@repo/ui/Buttons/radioButtonsGroup";
import { ComboboxField } from "@repo/ui/combobox/comboboxField";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import { IconExternalLink } from "@tabler/icons-react";
import { type FC, useMemo } from "react";
import { useNavigate } from "react-router";
import { useAllDataExtractionFieldDefinitionsQuery } from "../api/entities/dataExtractionField.js";
import { useAllKnowledgeBasesQuery } from "../api/entities/knowledgeBase.js";
import { appPaths } from "../app/appPaths.js";
import type { PlaybookEditorData } from "../hooks/usePlaybookEditor.js";
import { KnowledgeBaseSettingsForm } from "./recordingAssessmentEditor/knowledgeBaseSettingsForm.js";

interface DataExtractionFieldOption {
  id: string;
  name: string;
}

export interface PlaybookSettingsTabProps {
  data: PlaybookEditorData;
  onChange: (changes: Partial<PlaybookEditorData>) => void;
  /** Whether to show the knowledge base section (feature-flagged). */
  showsKnowledgeBase?: boolean;
}

const SPLITTING_OPTIONS: readonly {
  value: CustomerInteractionSplittingType;
  label: string;
  description: string;
}[] = [
  {
    value: CustomerInteractionSplitting.Never,
    label: "Never split",
    description: "Keep all calls from the same customer in one interaction.",
  },
  {
    value: CustomerInteractionSplitting.AlwaysSplit,
    label: "Always split",
    description: "Start a new interaction for every call.",
  },
];

/**
 * The "Settings" tab of the playbook editor. Each section is a controlled
 * input that commits every edit straight to the editor via `onChange` — the
 * same flow the rest of the editor uses, governed by the header's autosave and
 * Discard / Publish controls.
 */
export const PlaybookSettingsTab: FC<PlaybookSettingsTabProps> = ({
  data,
  onChange,
  showsKnowledgeBase = false,
}) => {
  const { data: allKnowledgeBases } = useAllKnowledgeBasesQuery();
  const navigate = useNavigate();
  const {
    data: allDataExtractionFieldDefinitions,
    isFetching: isLoadingDataExtractionFields,
  } = useAllDataExtractionFieldDefinitionsQuery();

  const dataExtractionFieldDefinitions = useMemo(
    () =>
      (allDataExtractionFieldDefinitions ?? []).filter(
        (definition) =>
          definition.resource_type === CustomFieldResourceType.GRADING,
      ),
    [allDataExtractionFieldDefinitions],
  );

  const dataExtractionFieldOptions = useMemo(
    () =>
      dataExtractionFieldDefinitions.map((definition) => ({
        id: definition.id,
        name: definition.display_name,
      })),
    [dataExtractionFieldDefinitions],
  );

  // Ids of fields that no longer exist have no chip to render, so drop them
  // the way the previous option-object mapping did.
  const selectedDataExtractionFieldIds = useMemo(
    () =>
      data.gradingDataExtractionFieldIds.filter((fieldId) =>
        dataExtractionFieldOptions.some((option) => option.id === fieldId),
      ),
    [data.gradingDataExtractionFieldIds, dataExtractionFieldOptions],
  );

  // `RadioButtonsGroup` keys options by their display `name`, so map between the
  // human-readable label and the underlying enum value.
  const selectedSplittingLabel =
    SPLITTING_OPTIONS.find(
      (option) => option.value === data.customerInteractionSplitting,
    )?.label ?? null;

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-8 px-4 py-8">
      {/* Playbook name */}
      <section>
        <div className="mb-3">
          <h2 className="text-base font-semibold text-foreground">
            Playbook name
          </h2>
        </div>
        <Input
          value={data.name}
          onChange={(event) => onChange({ name: event.target.value })}
          placeholder="Playbook name"
          aria-label="Playbook name"
          maxLength={200}
        />
      </section>

      {/* Data extraction fields */}
      <hr className="border-border-light" />
      <section>
        <div className="mb-3">
          <h2 className="text-base font-semibold text-foreground">
            Data extraction fields
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Select fields to extract from customer interactions graded with this
            playbook.
          </p>
        </div>
        {dataExtractionFieldDefinitions.length === 0 &&
        !isLoadingDataExtractionFields ? (
          <div className="text-sm text-muted-foreground">
            No customer interaction data extraction fields have been configured
            yet.{" "}
            <Button
              variant={ButtonVariant.Tertiary}
              size={Size.MEDIUM}
              icon={<IconExternalLink className="size-4" />}
              onClick={() => navigate(appPaths.settingsDataExtraction.build())}
            >
              Manage data extraction fields
            </Button>
          </div>
        ) : (
          <ComboboxField<DataExtractionFieldOption, "multiple">
            label="Fields"
            selectionMode="multiple"
            items={dataExtractionFieldOptions}
            getLabel={(option) => option.name}
            value={selectedDataExtractionFieldIds}
            onChange={(keys) => {
              onChange({ gradingDataExtractionFieldIds: keys.map(String) });
            }}
            placeholder={
              isLoadingDataExtractionFields
                ? "Loading fields..."
                : "Select data extraction fields"
            }
            isDisabled={isLoadingDataExtractionFields}
          />
        )}
      </section>

      {/* Knowledge base */}
      {showsKnowledgeBase ? (
        <>
          <hr className="border-border-light" />
          <section>
            <div className="mb-3">
              <h2 className="text-base font-semibold text-foreground">
                Knowledge base
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Connect knowledge bases to monitor adherence to handbooks and
                standard operating procedures.
              </p>
            </div>
            <KnowledgeBaseSettingsForm
              knowledgeBaseIds={data.knowledgeBaseIds}
              allKnowledgeBases={allKnowledgeBases ?? []}
              onUpdateKnowledgeBaseIds={(knowledgeBaseIds) =>
                onChange({ knowledgeBaseIds })
              }
              isReadOnly={false}
            />
          </section>
        </>
      ) : null}

      {/* Customer interaction splitting */}
      <hr className="border-border-light" />
      <section>
        <div className="mb-3">
          <h2 className="text-base font-semibold text-foreground">
            Customer interaction splitting
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Control when calls from the same customer are split into separate
            interactions. Applies to new recordings only.
          </p>
        </div>
        <RadioButtonsGroup
          options={SPLITTING_OPTIONS.map(({ label, description }) => ({
            name: label,
            description,
          }))}
          value={selectedSplittingLabel}
          onChange={(label) => {
            const option = SPLITTING_OPTIONS.find((o) => o.label === label);
            if (option) {
              onChange({ customerInteractionSplitting: option.value });
            }
          }}
        />
      </section>
    </div>
  );
};
